import { useEffect, useState } from 'react';
import { getWebApiBaseUrl } from '../config/apiBaseUrl';

const DEMO_MAPBOX_TOKEN =
  'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw';

/** @deprecated Use resolveMapboxToken() or useMapboxToken() — build-time token only. */
export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || DEMO_MAPBOX_TOKEN;

export function getMapboxTokenFromBuild(): string {
  return (process.env.REACT_APP_MAPBOX_TOKEN || '').trim();
}

export function isMapboxTokenValid(token?: string | null): boolean {
  const t = (token ?? getMapboxTokenFromBuild()).trim();
  if (!t || !t.startsWith('pk.')) return false;
  return t !== DEMO_MAPBOX_TOKEN && !t.includes(DEMO_MAPBOX_TOKEN.slice(0, 50));
}

let runtimeTokenPromise: Promise<string> | null = null;

/** Build-time REACT_APP_MAPBOX_TOKEN, else GET /api/config (MAPBOX_ACCESS_TOKEN on server). */
export async function resolveMapboxToken(): Promise<string> {
  const fromBuild = getMapboxTokenFromBuild();
  if (isMapboxTokenValid(fromBuild)) return fromBuild;
  if (typeof window === 'undefined') return '';

  if (!runtimeTokenPromise) {
    runtimeTokenPromise = (async () => {
      try {
        const base = getWebApiBaseUrl().replace(/\/$/, '');
        const res = await fetch(`${base}/config`, { credentials: 'same-origin' });
        if (!res.ok) return '';
        const data = await res.json();
        return (data.mapboxAccessToken || '').trim();
      } catch {
        return '';
      }
    })();
  }
  return runtimeTokenPromise;
}

export function useMapboxToken(): { token: string | null; loading: boolean; valid: boolean } {
  const buildToken = getMapboxTokenFromBuild();
  const [token, setToken] = useState<string | null>(() =>
    isMapboxTokenValid(buildToken) ? buildToken : null
  );
  const [loading, setLoading] = useState(() => !isMapboxTokenValid(buildToken));

  useEffect(() => {
    if (isMapboxTokenValid(buildToken)) return;
    let cancelled = false;
    resolveMapboxToken().then((t) => {
      if (cancelled) return;
      setToken(isMapboxTokenValid(t) ? t : null);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [buildToken]);

  return { token, loading, valid: isMapboxTokenValid(token) };
}

export const DEFAULT_MAP_CONFIG = {
  style: 'mapbox://styles/mapbox/streets-v12' as const,
  zoom: 13,
  minZoom: 10,
  maxZoom: 18,
};

export const handleMapboxError = (e: any, setError?: (error: string) => void): void => {
  console.error('Mapbox error:', e);
  const errorMsg = e.error?.message || '';
  const isTokenError =
    errorMsg.includes('token') || errorMsg.includes('401') || errorMsg.includes('Unauthorized');

  if (isTokenError) {
    const message =
      'Invalid Mapbox token. Set MAPBOX_ACCESS_TOKEN on the server or REACT_APP_MAPBOX_TOKEN in client/.env';
    if (setError) setError(message);
  } else if (setError) {
    setError(errorMsg || 'Failed to load map tiles');
  }
};
